import { useSocketIO } from "@features/builder/hooks/socketIOHooks"
import { GraphDocument } from "@features/builder/types"
import { ReactNode, useEffect, useState } from "react"
import * as Y from "yjs"
import { GraphDocumentContext } from "@features/builder/GraphDocumentProvider/context"

type GraphDocumentProviderProps = {
  children?: ReactNode
  graphKey: string | undefined
}

const setupGraphSync = (
  socket: ReturnType<typeof useSocketIO>,
  graphKey: string,
  setGraphDoc: (doc: GraphDocument | undefined) => void,
) => {
  let currentDoc: Y.Doc | undefined

  const handleLocalDocUpdate = (update: Uint8Array) => {
    if (import.meta.env.VITE_GRAPH_BUILDER_SEND_UPDATES !== "true") return
    if (!socket.connected) return

    socket.emit("crdt_update", graphKey, update)
  }

  const replaceGraphDoc = (response: ArrayBuffer) => {
    if (currentDoc) {
      currentDoc.off("update", handleLocalDocUpdate)
      currentDoc.destroy()
    }

    const doc = new Y.Doc()
    Y.applyUpdate(doc, new Uint8Array(response))
    doc.on("update", handleLocalDocUpdate)
    currentDoc = doc
    setGraphDoc(doc as GraphDocument)
  }

  const handleRemoteDocUpdate = (response: ArrayBuffer) => {
    if (!currentDoc) return
    Y.applyUpdate(currentDoc, new Uint8Array(response))
  }

  const fetchGraphSnapshot = () => {
    socket.emit("crdt_graph_get", { id: graphKey })
  }

  socket.on("crdt_graph_get", replaceGraphDoc)
  socket.on("crdt_update", handleRemoteDocUpdate)
  socket.on("connect", fetchGraphSnapshot)

  fetchGraphSnapshot()

  return () => {
    socket.off("crdt_graph_get", replaceGraphDoc)
    socket.off("crdt_update", handleRemoteDocUpdate)
    socket.off("connect", fetchGraphSnapshot)

    if (currentDoc) {
      currentDoc.off("update", handleLocalDocUpdate)
      currentDoc.destroy()
    }
  }
}

const GraphDocumentProvider = (props: GraphDocumentProviderProps) => {
  const [graphDoc, setGraphDoc] = useState<GraphDocument | undefined>(undefined)
  const socket = useSocketIO()

  useEffect(() => {
    setGraphDoc(undefined)
    if (!props.graphKey) return

    return setupGraphSync(socket, props.graphKey, setGraphDoc)
  }, [props.graphKey, socket])

  return (
    <GraphDocumentContext.Provider value={graphDoc}>{props.children}</GraphDocumentContext.Provider>
  )
}

export default GraphDocumentProvider
